import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Box,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import useAdd from "../../api/useAdd";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";

const StockAdd = ({ open, onClose, token }) => {
  const { t } = useTranslation();
  const { handleAdd, loading, resetForm } = useAdd("shop/stocks", token);

  // Fetch shops and purchase items for selection
  const { data: shops, loading: shopLoading } = useFetchObjects(
    ["shop"],
    "shop/shop",
    token
  );
  const { data: purchaseItems, loading: purchaseItemLoading } = useFetchObjects(
    ["purchase_item"],
    "shop/purchase_items",
    token
  );

  const [selectedShop, setSelectedShop] = useState("");
  const [selectedPurchaseItem, setSelectedPurchaseItem] = useState("");
  const [quantity, setQuantity] = useState("");

  const shopsList = Array.isArray(shops) ? shops : shops?.results || [];
  const purchaseItemsList = Array.isArray(purchaseItems)
    ? purchaseItems
    : purchaseItems?.results || [];

  const getShopLabel = (shop) => shop?.name || "";
  const getPurchaseItemLabel = (item) =>
    item?.product?.name
      ? `${item.product.name}${item?.product?.code ? ` (${item.product.code})` : ""}`
      : t("stock.unknownPurchaseItem");

  const selectedShopOption =
    shopsList.find((shop) => String(shop.id) === String(selectedShop)) || null;
  const selectedPurchaseItemOption =
    purchaseItemsList.find((item) => String(item.id) === String(selectedPurchaseItem)) || null;

  const handleSubmit = () => {
    const formData = new FormData();
    formData.append("shop", selectedShop);
    formData.append("purchase_item", selectedPurchaseItem);
    formData.append("quantity", quantity);
    handleAdd(formData);
  };

  useEffect(() => {
    if (resetForm) {
      setSelectedShop("");
      setSelectedPurchaseItem("");
      setQuantity("");
      onClose();
    }
  }, [resetForm, onClose]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("stock.addStock")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          <Autocomplete
            options={shopsList}
            value={selectedShopOption}
            onChange={(_, value) => setSelectedShop(value ? String(value.id) : "")}
            getOptionLabel={getShopLabel}
            isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
            loading={shopLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                margin="dense"
                label={t("stock.selectShop")}
                fullWidth
                required
              />
            )}
          />
          <Autocomplete
            options={purchaseItemsList}
            value={selectedPurchaseItemOption}
            onChange={(_, value) =>
              setSelectedPurchaseItem(value ? String(value.id) : "")
            }
            getOptionLabel={getPurchaseItemLabel}
            isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
            loading={purchaseItemLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                margin="dense"
                label={t("stock.selectPurchaseItem")}
                fullWidth
                required
              />
            )}
          />
          <TextField
            margin="dense"
            label={t("stock.quantity")}
            type="number"
            fullWidth
            required
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("stock.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            loading || !selectedShop || !selectedPurchaseItem || !quantity
          }
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("stock.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StockAdd;
